import { useState, useEffect } from 'react';
import { apiFetch } from '../api';
import WeatherIcon from './WeatherIcon';

const WEATHER_CODES = {
  0: { label: 'Clear', icon: 'sunny' },
  1: { label: 'Mostly Clear', icon: 'sunny' },
  2: { label: 'Partly Cloudy', icon: 'partly-cloudy' },
  3: { label: 'Overcast', icon: 'cloudy' },
  45: { label: 'Foggy', icon: 'foggy' },
  48: { label: 'Icy Fog', icon: 'foggy' },
  51: { label: 'Light Drizzle', icon: 'rainy' },
  53: { label: 'Drizzle', icon: 'rainy' },
  55: { label: 'Heavy Drizzle', icon: 'rainy' },
  61: { label: 'Light Rain', icon: 'rainy' },
  63: { label: 'Rain', icon: 'rainy' },
  65: { label: 'Heavy Rain', icon: 'rainy' },
  71: { label: 'Light Snow', icon: 'snowy' },
  73: { label: 'Snow', icon: 'snowy' },
  75: { label: 'Heavy Snow', icon: 'snowy' },
  80: { label: 'Showers', icon: 'rainy' },
  81: { label: 'Moderate Showers', icon: 'rainy' },
  82: { label: 'Heavy Showers', icon: 'rainy' },
  85: { label: 'Snow Showers', icon: 'snowy' },
  86: { label: 'Heavy Snow Showers', icon: 'snowy' },
  95: { label: 'Thunderstorm', icon: 'stormy' },
  96: { label: 'Hail Thunderstorm', icon: 'stormy' },
  99: { label: 'Heavy Hail Thunderstorm', icon: 'stormy' },
};

function WeatherWidget({ refreshKey }) {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchWeather() {
      try {
        const res = await apiFetch('/api/weather');
        const data = await res.json();
        setWeather(data);
      } catch (err) {
        console.error('Failed to fetch weather:', err);
      } finally {
        setLoading(false);
      }
    }
    fetchWeather();
  }, [refreshKey]);

  if (loading) return <section className="card"><h2>Weather</h2><p className="loading">Loading...</p></section>;
  if (!weather) return <section className="card"><h2>Weather</h2><p className="error">Could not load weather</p></section>;

  const current = weather.current;
  const daily = weather.daily;
  const hourly = weather.hourly;
  const currentCode = WEATHER_CODES[current.weather_code] || { label: 'Unknown', icon: 'cloudy' };

  // Get remaining hours today in CDT
  const nowCDT = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Chicago' }));
  const remainingHours = [];
  if (hourly) {
    const currentHour = nowCDT.getHours();
    const todayStr = nowCDT.toLocaleDateString('en-CA', { timeZone: 'America/Chicago' });

    hourly.time.forEach((t, i) => {
      const d = new Date(t);
      const dateStr = d.toLocaleDateString('en-CA', { timeZone: 'America/Chicago' });
      const hour = d.getHours();
      if (dateStr === todayStr && hour >= currentHour) {
        remainingHours.push({
          hour,
          label: d.toLocaleTimeString('en-US', { hour: 'numeric', timeZone: 'America/Chicago' }),
          temp: Math.round(hourly.temperature_2m[i]),
        });
      }
    });
  }

  // Compute graph min/max
  const temps = remainingHours.map((h) => h.temp);
  const minTemp = Math.min(...temps, Math.round(current.temperature_2m) - 5);
  const maxTemp = Math.max(...temps, Math.round(current.temperature_2m) + 5);
  const range = maxTemp - minTemp || 1;

  return (
    <section className="card weather-section-v2">
      <div className="weather-top">
        <div className="weather-icon-big">
          <WeatherIcon type={currentCode.icon} size={80} />
        </div>
        <div className="weather-current-v2">
          <span className="weather-temp-big">{Math.round(current.temperature_2m)}°</span>
          <span className="weather-desc-v2">{currentCode.label}</span>
          <div className="weather-details-v2">
            <span>Feels {Math.round(current.apparent_temperature)}°</span>
            <span>{current.relative_humidity_2m}% hum</span>
            <span>{Math.round(current.wind_speed_10m)} mph</span>
          </div>
        </div>
        <div className="weather-hilo">
          <span className="weather-hi">H: {Math.round(daily.temperature_2m_max[0])}°</span>
          <span className="weather-lo">L: {Math.round(daily.temperature_2m_min[0])}°</span>
        </div>
      </div>

      {remainingHours.length > 1 && (
        <div className="weather-graph">
          <svg viewBox={`0 0 ${remainingHours.length * 40} 80`} className="weather-graph-svg" preserveAspectRatio="none">
            {/* Area fill */}
            <path
              d={
                `M 0 ${80 - ((remainingHours[0].temp - minTemp) / range) * 60 - 10} ` +
                remainingHours.map((h, i) => {
                  const x = i * 40 + 20;
                  const y = 80 - ((h.temp - minTemp) / range) * 60 - 10;
                  return `L ${x} ${y}`;
                }).join(' ') +
                ` L ${(remainingHours.length - 1) * 40 + 20} 80 L 0 80 Z`
              }
              fill="url(#tempGradient)"
            />
            <defs>
              <linearGradient id="tempGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#818cf8" stopOpacity="0.3" />
                <stop offset="100%" stopColor="#818cf8" stopOpacity="0" />
              </linearGradient>
            </defs>
            {/* Line */}
            <polyline
              points={remainingHours.map((h, i) => {
                const x = i * 40 + 20;
                const y = 80 - ((h.temp - minTemp) / range) * 60 - 10;
                return `${x},${y}`;
              }).join(' ')}
              fill="none"
              stroke="#818cf8"
              strokeWidth="2"
              strokeLinejoin="round"
            />
            {/* Dots */}
            {remainingHours.map((h, i) => {
              const x = i * 40 + 20;
              const y = 80 - ((h.temp - minTemp) / range) * 60 - 10;
              return <circle key={i} cx={x} cy={y} r="3" fill="#818cf8" />;
            })}
          </svg>
          <div className="weather-graph-labels">
            {remainingHours.map((h, i) => (
              <div key={i} className="weather-graph-label" style={{ width: 40 }}>
                <span className="graph-temp">{h.temp}°</span>
                <span className="graph-time">{h.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="weather-forecast-row">
        {daily.time.slice(1, 5).map((date, i) => {
          const code = WEATHER_CODES[daily.weather_code[i + 1]] || { label: 'Unknown', icon: 'cloudy' };
          const dayName = new Date(date + 'T12:00:00').toLocaleDateString('en-US', { timeZone: 'America/Chicago', weekday: 'short' });
          return (
            <div key={date} className="forecast-v2-day">
              <span className="forecast-v2-name">{dayName}</span>
              <WeatherIcon type={code.icon} size={24} />
              <span className="forecast-v2-temps">
                {Math.round(daily.temperature_2m_max[i + 1])}° / {Math.round(daily.temperature_2m_min[i + 1])}°
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export default WeatherWidget;
